import React from "react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid,
} from "recharts";
import { Radar as RadarIcon, TrendingUp } from "lucide-react";
import { GOLD, CRIMSON } from "./AnalysisStation";

const MONO = '"Roboto Mono", monospace';
const PANEL = {
  background: "linear-gradient(180deg, rgba(22,22,22,0.92), rgba(8,8,8,0.96))",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.06), 0 18px 50px rgba(0,0,0,0.55)",
};
const tip = {
  contentStyle: { background: "#0a0a0a", border: `1px solid ${GOLD}66`, borderRadius: 4, fontFamily: MONO, fontSize: 10, letterSpacing: 1 },
  labelStyle: { color: GOLD }, itemStyle: { color: "#fff" },
};

const Head = ({ icon: Icon, children, right }) => (
  <div className="flex items-center justify-between gap-2 mb-2">
    <span className="flex items-center gap-2 font-mono text-[9px] tracking-[0.3em] uppercase text-[#F5C518]"><Icon size={12} /> {children}</span>
    {right}
  </div>
);

export const Readout = ({ k, v }) => {
  const color = v >= 9 ? GOLD : v >= 6 ? "#fff" : CRIMSON;
  return (
    <div data-testid={`radar-readout-${k.toLowerCase()}`} className="font-mono">
      <div className="flex items-baseline justify-between">
        <span className="text-[8px] tracking-[0.25em] uppercase text-white/45">{k}</span>
        <span className="text-lg leading-none font-bold" style={{ color, textShadow: `0 0 6px ${color}, 0 0 16px ${color}88` }}>{v}<span className="text-[8px] text-white/30">/10</span></span>
      </div>
      <div className="mt-1 h-[3px] bg-white/10 rounded-full overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${v * 10}%`, background: `linear-gradient(90deg, ${CRIMSON}, ${GOLD})`, boxShadow: `0 0 6px ${GOLD}, 0 0 14px ${GOLD}99, 0 0 4px ${CRIMSON}` }} />
      </div>
    </div>
  );
};

export const ProRadar = ({ engine: e }) => {
  const data = [{ axis: "BALANCE", v: e.balance }, { axis: "EXECUTION", v: e.execution }, { axis: "GAME_PACE", v: e.pace }];
  const composite = ((e.balance + e.execution + e.pace) / 3).toFixed(1);
  return (
    <div data-testid="pro-radar" className="rounded-xl border border-[#F5C518]/25 p-3 sm:p-4 neon-panel-gold" style={PANEL}>
      <Head icon={RadarIcon} right={<span className="font-mono text-[8px] tracking-[0.2em] uppercase text-white/50">Composite <span className="text-[#F5C518] font-bold text-xs neon-text-gold">{composite}</span></span>}>
        Pro_Scout · Technical_Radar
      </Head>
      <div className="grid grid-cols-1 sm:grid-cols-[1.35fr_1fr] gap-3 items-center">
        <div className="neon-gold" style={{ height: 210 }}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={data} outerRadius="82%" margin={{ top: 4, bottom: 4 }}>
              <defs>
                <radialGradient id="nxRadarFill" cx="50%" cy="50%" r="70%">
                  <stop offset="0%" stopColor={GOLD} stopOpacity={0.6} />
                  <stop offset="55%" stopColor={CRIMSON} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={CRIMSON} stopOpacity={0.08} />
                </radialGradient>
              </defs>
              <PolarGrid gridType="polygon" stroke="rgba(245,197,24,0.2)" />
              <PolarAngleAxis dataKey="axis" tick={{ fill: "rgba(245,197,24,0.85)", fontSize: 9, fontFamily: MONO, letterSpacing: 2 }} />
              <PolarRadiusAxis domain={[0, 10]} tickCount={6} angle={90} axisLine={false} tick={{ fill: "rgba(255,255,255,0.28)", fontSize: 7, fontFamily: MONO }} />
              <Radar dataKey="v" stroke={GOLD} strokeWidth={2} fill="url(#nxRadarFill)" fillOpacity={1}
                dot={{ r: 3.5, fill: "#0a0a0a", stroke: GOLD, strokeWidth: 2 }} isAnimationActive />
            </RadarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-3">
          <Readout k="Balance" v={e.balance} />
          <Readout k="Execution" v={e.execution} />
          <Readout k="Game Pace" v={e.pace} />
        </div>
      </div>
    </div>
  );
};

export const ProTrend = ({ trend, label }) => (
  <div data-testid="pro-trend" className="rounded-xl border border-[#F5C518]/20 p-3 sm:p-4 neon-panel-red" style={PANEL}>
    <Head icon={TrendingUp}>Performance_Trend · {label || "This Drill"}</Head>
    {trend.length < 2 ? (
      <p className="font-mono text-[10px] tracking-widest uppercase text-white/35 py-8 text-center">Scan this drill again to build your trend line.</p>
    ) : (
      <div className="neon-gold" style={{ height: 180 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={trend} margin={{ top: 10, right: 14, bottom: 4, left: -18 }}>
            <CartesianGrid strokeDasharray="3 7" stroke="rgba(255,255,255,0.07)" />
            <XAxis dataKey="date" tick={{ fill: "rgba(245,197,24,0.55)", fontSize: 8, fontFamily: MONO }} axisLine={{ stroke: "rgba(245,197,24,0.15)" }} tickLine={false} />
            <YAxis domain={[0, 10]} tick={{ fill: "rgba(255,255,255,0.35)", fontSize: 8, fontFamily: MONO }} axisLine={false} tickLine={false} />
            <Tooltip {...tip} formatter={(v) => [`${v}/10`, "SCORE"]} />
            <ReferenceLine y={9} stroke={GOLD} strokeDasharray="4 4" strokeOpacity={0.55} label={{ value: "ELITE_LINE", fill: GOLD, fontSize: 7, fontFamily: MONO, position: "insideTopRight" }} />
            <Line type="monotone" dataKey="score" stroke={GOLD} strokeWidth={2.5} dot={{ r: 3, fill: "#0a0a0a", stroke: GOLD, strokeWidth: 2 }} activeDot={{ r: 5, fill: CRIMSON, stroke: GOLD }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    )}
  </div>
);
